import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { AddActionItemAzureDevOpsWorkItemDto, FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  actionItemGuid: string;
  azureDevOpsWorkItemCollectionGuid: string | null;
  workItemId: number;
  includeChildren: boolean;
};

const useAddActionItemAzureDevOpsWorkItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function addActionItemAzureDevOpsWorkItemRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: AddActionItemAzureDevOpsWorkItemDto = {
      workItemId: variables.workItemId,
      includeChildren: variables.includeChildren,
    };

    const response = await fetch(
      `${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/azure-devops-work-items`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { addActionItemAzureDevOpsWorkItemRequest };
};

const useAddActionItemAzureDevOpsWorkItem = () => {
  const { addActionItemAzureDevOpsWorkItemRequest } = useAddActionItemAzureDevOpsWorkItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: addActionItemAzureDevOpsWorkItemRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));

      if (variables.azureDevOpsWorkItemCollectionGuid) {
        queryClient.removeQueries(
          queryKeys.azureDevOpsWorkItems.getAzureDevOpsWorkItemCollection(variables.azureDevOpsWorkItemCollectionGuid)
        );
      }
    },
  });
};

export { useAddActionItemAzureDevOpsWorkItem };
